import {TermsAndConditionsModel} from "src/app/models/common/terms-and-conditions.model";
import {DropoffScheduleError, EarlyReturnOptions, PickupScheduleError} from "./lease-quote-table/lease-quote";

export interface IBaseQuoteModel {
    id: string;
    request: string;
    quote_items: IOneWayQuoteItemModel[] | ITradeQuoteItemModel[] | ILeaseQuoteItemModel[];
    quote_valid_till: Date;
    document: UploadedDocument;
    uploaded_documents: UploadedDocument[];
    terms_and_condition_text: string;
    terms_and_condition_fk: TermsAndConditionsModel;
    remarks: string;
    quote_number: string;
    created_by: CreatedBy;
    quotesChargesList?: RequestQuotesChargesList[]
}

export interface IOneWayQuoteModel extends IBaseQuoteModel {
    payee: string,
    insurance_premium_currency: string,
    insurance_premium: number,
    insurance: InsuranceModel;
}

export interface ILeaseQuoteModel extends IBaseQuoteModel {
    lease_agreement: ILeaseAgreement;
    pickup_schedules: IPickupSchedule[];
    dropoff_schedules: IDropoffSchedule[];
}

export interface IBaseQuoteItem {
    id: string;
    requirement: string;
    quantity: number;
    created_by: CreatedBy;
    quote: string;
    container_size_type: ContainerSizeType;
    container_condition: ContainerCondition;
}

export interface IOneWayQuoteItemModel extends IBaseQuoteItem {
    min_free_days: number;
    max_free_days: number;
    pickup_charge_currency: string;
    pickup_charge: number;
    min_per_diem_currency: string;
    min_per_diem: number;
    max_per_diem_currency: string;
    max_per_diem: number;
    dpp_charge_currency: string;
    dpp_charge: number;
    storage_charge_currency: string;
    storage_charge: number;
    dropoff_charge_currency: string;
    dropoff_charge: number;
    cancellation_charge_currency: string;
    cancellation_charge: number;
    drv_new_build_price_currency: string;
    drv_new_build_price: number;
    min_late_charge_currency: string;
    min_late_charge: number;
    max_late_charge_currency: string;
    max_late_charge: number;
    drv_depreciation_per_year: number;
    drv_max_depreciation_percent: number;
    pol_list: LocationModel[];
    pod_list: LocationModel[];
}

export interface ITradeQuoteItemModel extends IBaseQuoteItem {
    price_currency: string;
    price: number;
    negotiation_percent: number;
    min_manufacturing_year: number;
    max_manufacturing_year: number;
    pickup_ready_date: string;
    storage_charge_currency: string;
    storage_charge: number;
    csc_revalidation_required: boolean;
    csc_revalidation_charge_currency: string;
    csc_revalidation_charge: number;
    csc_validation_included: boolean;
    color_preference: ColorPreference;
    purchase_locations: LocationModel[];
    container_images: UploadedDocument[];
}

export interface ILeaseQuoteItemModel extends IBaseQuoteItem {
    is_ero_enabled: EarlyReturnOptions;
    ero: IEro[];
    updated_at: string;
    is_active: boolean;
    per_diem_charge: number;
    per_diem_charge_currency: string;
    build_up_period_days: number;
    build_down_period_days: number;
    post_build_down_per_diem_charge: number;
    post_build_down_per_diem_charge_currency: string;
    minimum_rental_period_days: number;
    container_deemed_lost_after_days: number;
    notice_period_amendment_delivery_days: number;
    notice_period_amendment_return_days: number;
    drv_new_build_price: number;
    drv_new_build_price_currency: string;
    drv_depreciation_per_year: number;
    drv_max_depreciation_percent: number;
    leasing_period_days: number;
    pol_list: LocationModel[];
    pod_list: LocationModel[];
}


export interface LeasePurpose {
    id: string;
    is_active: boolean;
    group: string;
    value: string;
    description: string;
    sequence_no: number;
    dev_value: string;
}

export interface Picture {
    id: string;
    is_active: boolean;
    document_file: string;
    extension: string;
    original_filename: string;
    size: number;
    remarks: string;
    document_type: string;
}

export interface CreatedBy {
    id: string;
    picture: Picture;
    first_name: string;
    last_name: string;
    initials: string;
    email: string;
    gender: string;
}

export interface ILeaseAgreement {
    id: string;
    lease_purpose: LeasePurpose | any;
    documents: Document[];
    is_active: boolean;
    lease_type: string | any;
    billing_cycle: string | any;
    billing_date: number | any;
    security_deposit_currency: string;
    security_deposit: string;
    agreement_number: string;
    agreement_starts_from: string;
    agreement_ends_on: string;
    currency: string;
    remarks: string;
    lessor_company_name: string;
    lessor_contact_person_name: string;
    lessor_contact_person_email: string;
    lessor_contact_person_phone: string;
    lessee_company_name: string;
    lessee_contact_person_name: string;
    lessee_contact_person_email: string;
    lessee_contact_person_phone: string;
    lessor_company_address: string;
    lessee_company_address: string;
    request: string;
}

export interface IContainerCondition {
    id: string;
    is_active: boolean;
    group: string;
    value: string;
    description: string;
    sequence_no: number;
    dev_value: string;
}

export interface ManHourCharge {
    id: string;
    location: string;
    is_active: boolean;
    man_hour_charges_currency: string;
    man_hour_charges: string;
    effective_from_date: string;
    effective_to_date: string;
    depot: string;
}

export interface LocationModel {
    id: string;
    name: string;
    display_name: string;
    name_display: string;
    man_hour_charge: ManHourCharge;
}

export interface IEro {
    id: string;
    amount_currency: string;
    amount: number;
    days: number;
    sequence_no: number;
    penalty_from: any;
    fromDays: number;
    toDays: number;
}

export interface IPickupSchedule {
    id: string;
    port: LocationModel | any;
    client_id: string;
    is_active: boolean;
    per_month_permitted_quantity: number;
    total_permitted_quantity: number;
    handling_charge_currency: string;
    handling_charge: string;
    remarks: string;
    pickup_charge_currency: string;
    pickup_charge: string;
    requirement: any;
    hideRow: string;
    error: PickupScheduleError;
}

export interface IDropoffSchedule {
    id: string;
    port: LocationModel | any;
    client_id: string;
    is_active: boolean;
    per_month_permitted_quantity: number;
    total_permitted_quantity: number;
    handling_charge_currency: string;
    handling_charge: string;
    remarks: string;
    dropoff_charge_currency: string;
    dropoff_charge: string;
    requirement: any;
    hideRow: string;
    error: DropoffScheduleError;
}

export interface UploadedDocument {
    id: string;
    created_by: CreatedBy;
    document_type: DocumentType;
    is_active: boolean;
    document_file: string;
    extension: string;
    original_filename: string;
    size: number;
    remarks: string;
}

export interface Item {
    id: string;
    updated_at: string;
    created_at: string;
    is_active: boolean;
    name: string;
    short_description: string;
    created_by: string;
    updated_by: string;
}

export interface AmountUnitModel {
    id: string;
    updated_at: string;
    created_at: string;
    is_active: boolean;
    group: string;
    value: string;
    description: string;
    sequence_no: number;
    dev_value: string;
    created_by: string;
    updated_by: string;
}

export interface IInsuranceModel {
    id: string;
    items: Item[];
    amount_unit: AmountUnitModel;
    updated_at: string;
    created_at: string;
    is_active: boolean;
    name: string;
    short_description: string;
    max_coverage_days: number;
    amount_currency: string;
    amount: number;
    visible_to_internal: boolean;
    visible_to_external: boolean;
    sequence: number;
    created_by: string;
    updated_by: string;
    icon: string;
    term_and_conditions: string;
}


export interface IContainerSizeType {
    id: string;
    display: string;
    updated_at: Date;
    created_at: Date;
    is_active: true,
    created_by: string;
    updated_by: string;
    size: string;
    type: string;
}

export interface IColourPreference {
    id: string;
    name: string;
    updated_at: Date;
    created_at: Date;
    is_active: true;
    created_by: string;
    updated_by: string;
}

export class ContainerCondition implements IContainerCondition {
    id: string;
    is_active: boolean;
    group: string;
    value: string;
    description: string;
    sequence_no: number;
    dev_value: string;

    constructor() {
        this.id = null;
        this.is_active = null;
        this.group = null;
        this.value = null;
        this.description = null;
        this.sequence_no = null;
        this.dev_value = null;
    }
}

export class ContainerSizeType implements IContainerSizeType {
    id: string;
    display: string;
    updated_at: Date;
    created_at: Date;
    is_active: true;
    created_by: string;
    updated_by: string;
    size: string;
    type: string;

    constructor() {
        this.id = null;
        this.display = null;
        this.updated_at = null;
        this.created_at = null;
        this.is_active = null;
        this.created_by = null;
        this.updated_by = null;
        this.size = null;
        this.type = null;
    }
}

export class InsuranceModel implements IInsuranceModel {
    id: string;
    items: Item[];
    amount_unit: AmountUnitModel;
    updated_at: string;
    created_at: string;
    is_active: boolean;
    name: string;
    short_description: string;
    max_coverage_days: number;
    amount_currency: string;
    amount: number;
    visible_to_internal: boolean;
    visible_to_external: boolean;
    sequence: number;
    created_by: string;
    updated_by: string;
    icon: string;
    term_and_conditions: string;

    constructor() {
        this.id = null;
        this.items = [];
        this.amount_unit = null;
        this.updated_at = null;
        this.created_at = null;
        this.is_active = null;
        this.name = null;
        this.short_description = null;
        this.max_coverage_days = null;
        this.amount_currency = null;
        this.amount = null;
        this.visible_to_internal = null;
        this.visible_to_external = null;
        this.sequence = null;
        this.created_by = null;
        this.updated_by = null;
        this.icon = null;
        this.term_and_conditions = null;
    }
}

export class ColorPreference implements IColourPreference {
    id: string;
    name: string;
    updated_at: Date;
    created_at: Date;
    is_active: true;
    created_by: string;
    updated_by: string;

    constructor() {
        this.id = null;
        this.name = null;
        this.updated_at = null;
        this.created_at = null;
        this.is_active = null;
        this.created_by = null;
        this.updated_by = null;
    }
}

export interface PenaltyTypeOptions {
    value: string;
    display_name: string
}

export class RequestQuotesChargesList {
    id: string
    charge: Charge
    service_type: ServiceType
    is_active: boolean
    auto_append_to_quotes: boolean
    is_consolidated_charge: boolean

    constructor(data = null) {
        this.id = data.id;
        this.charge = data.id;
        this.service_type = data.service_type;
        this.is_active = data.is_active;
        this.auto_append_to_quotes = data.auto_append_to_quotes;
        this.is_consolidated_charge = data.is_consolidated_charge;
    }
}

export interface Charge {
    hsn_code: HsnCode
    uom: Uom
    description: string
    company: ChargeCompany
    charge_code: string
    charge_head: string
    print_name: string
    charge_category: ChargeCategory
    charge_group: ChargeGroup
    is_grouped: boolean
    receivable_payable: ReceivablePayable
    is_incidental: boolean
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    applicability: string
}

export interface HsnCode {
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    code: string
    description: string
    max_tax_rate: string
    active_since: string
    created_by: string
    updated_by: string
    type: string
    main_charge_type: string
}

export interface Uom {
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    group: string
    value: string
    description: string
    sequence_no: number
    dev_value: string
    created_by: string
    updated_by: string
}

export interface ChargeCompany {
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    name: string
    website: string
    legal_name: string
    trade_name: string
    credit_days: number
    credit_limits_currency: string
    credit_limits: string
    virtual_account: string
    bank_name: string
    address: string
    about: string
    data_audit_status: number
    data_audit_done_on: string
    primary_contact_no: string
    secondary_contact_no: string
    primary_email: string
    secondary_email: string
    remarks: string
    master_agreement_no: string
    master_agreement_valid_till: string
    request_count: number
    is_virgin: boolean
    created_by: string
    updated_by: string
    group: string
    company_type: string
    industry_type: string
    state: string
    data_audit_done_by: string
    logo: string
    cover_picture: string
    country: string
    master_agreement_document: string
    consolidated_kyc_status: string
    constitution: string
    documents: string[]
}

export interface ChargeCategory {
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    charge_category_name: string
    description: string
    created_by: string
    updated_by: string
    company: string
}

export interface ChargeGroup {
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    name: string
    description: string
    created_by: string
    updated_by: string
}

export interface ReceivablePayable {
    id: string
    updated_at: string
    created_at: string
    is_active: boolean
    group: string
    value: string
    description: string
    sequence_no: number
    dev_value: string
    created_by: string
    updated_by: string
}

export interface ServiceType {
    id: string
    name_display: string
    updated_at: string
    created_at: string
    is_active: boolean
    name: string
    dev_name: string
    created_by: string
    updated_by: string
}

export class QuoteChargeMaster {
    id: string;
    quote_charge: RequestQuotesChargesList;
    is_active: boolean;
    amount: number;
    amount_currency: string;
    error: QuoteChargeMasterError;

    constructor(data = null) {
        this.id = data ? data.id : null;
        this.quote_charge = data ? data.quote_charge : null;
        this.is_active = data ? data.is_active : null;
        this.amount = data ? data.amount : null;
        this.amount_currency = data ? data.amount_currency : 'USD';
        this.error = new QuoteChargeMasterError();
    }
}

export class QuoteChargeMasterError {
    id: string[];
    quote_charge: [];
    is_active: [];
    amount: [];
    amount_currency: [];

    constructor() {
        this.id = [];
        this.quote_charge = [];
        this.is_active = [];
        this.amount = [];
        this.amount_currency = [];
    }
}

export class EmptyRepoQuoteChargeMaster {
    id: string;
    quote_charge: RequestQuotesChargesList;
    is_active: boolean;
    amount: number;
    amount_currency: string;
    paid_to: any;
    error: EmptyRepoQuoteChargeMasterError;

    constructor(data = null) {
        this.id = data ? data.id : null;
        this.quote_charge = data ? data.quote_charge : null;
        this.is_active = data ? data.is_active : null;
        this.amount = data ? data.amount : null;
        this.amount_currency = data ? data.amount_currency : 'USD';
        this.paid_to = data ? data.paid_to : null;
        this.error = new EmptyRepoQuoteChargeMasterError();
    }
}

export class EmptyRepoQuoteChargeMasterError {
    id: string[];
    quote_charge: [];
    is_active: [];
    amount: [];
    amount_currency: [];
    paid_to: [];

    constructor() {
        this.id = [];
        this.quote_charge = [];
        this.is_active = [];
        this.amount = [];
        this.amount_currency = [];
        this.paid_to = [];
    }
}

export class QuoteEditPayload {
    quantity?: string;
    condition?: string;
    container_size_type?: string;
    per_diem?: string;
    min_per_diem?: string;
    max_per_diem?: string;
    csc_revalidation_required?: string;
    pol_list?: any[];
    pod_list?: any[];
    free_storage_days?: string;
    min_per_diem_free_days?: string;
    max_per_diem_free_days?: string;
    minimum_rental_period_days?: string;
    storage_charge?: string;
    purpose?: string;
    leasing_period_days?: string;

    constructor() {
        this.quantity = null
        this.condition = null
        this.container_size_type = null
        this.per_diem = null
        this.min_per_diem = null
        this.max_per_diem = null
        this.csc_revalidation_required = null
        this.pol_list = []
        this.pod_list = []
        this.free_storage_days = null
        this.min_per_diem_free_days = null
        this.max_per_diem_free_days = null
        this.minimum_rental_period_days = null
        this.storage_charge = null
        this.purpose = null
        this.leasing_period_days = null
    }

    setEmptyRepoEditPayload(data: any) {
        this.quantity = data.quantity;
        this.condition = null;
        this.container_size_type = data.container_size_type?.id;
        this.pol_list = data.pol_list;
        this.pod_list = data.pod_list;
    }

    getEmptyRepoQuoteEditPayload() {
        return {
            quantity: this.quantity,
            condition: null,
            container_size_type: this.container_size_type,
            pol_list: this.pol_list.map(item => item?.id),
            pod_list: this.pod_list.map(item => item?.id)
        }
    }
}
